import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Video, FileDown, Send, Save, Clock, PhoneOff, Maximize2, Mic, Square, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, refreshSession } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SessionTools } from "../components/SessionTools";
import { ConsultationDocs } from "../components/ConsultationDocs";
import { SignaturePad } from "../components/SignaturePad";

function loadDaily() {
  return new Promise((resolve, reject) => {
    if (window.DailyIframe) return resolve(window.DailyIframe);
    const s = document.createElement("script");
    s.src = "https://unpkg.com/@daily-co/daily-js";
    s.onload = () => resolve(window.DailyIframe); s.onerror = reject;
    document.body.appendChild(s);
  });
}
const EF = ["anamnese", "examen", "diagnostic", "ordonnance", "examens_complementaires", "conduite", "prochain_rdv"];
const LABELS = { anamnese: "Anamnèse", examen: "Examen clinique (à distance)", diagnostic: "Diagnostic(s) probable(s)", ordonnance: "Ordonnance (médicaments, posologie, durée)", examens_complementaires: "Examens complémentaires", conduite: "Conduite à tenir", prochain_rdv: "Prochain rendez-vous" };

export default function Teleconsultation() {
  const { id } = useParams();
  const [appt, setAppt] = useState(null);
  const [cr, setCr] = useState({});
  const [seconds, setSeconds] = useState(0);
  const [room, setRoom] = useState(null);
  const [busy, setBusy] = useState(false);
  const frameRef = useRef(null); const callRef = useRef(null);
  const boardPngRef = useRef(null);
  const videoWrapRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  const [recField, setRecField] = useState(null);
  const [recBusy, setRecBusy] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const mediaRecorderRef = useRef(null); const audioChunksRef = useRef([]);
  useEffect(() => {
    const onFs = () => setIsFs(document.fullscreenElement === videoWrapRef.current);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const goFullscreen = () => {
    const el = videoWrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) { document.exitFullscreen?.(); }
    else { (el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen)?.call(el); }
  };

  useEffect(() => {
    api.get("/admin/visio/appointments").then((r) => {
      const a = r.data.find((x) => x.id === id);
      setAppt(a || false);
      if (a?.cr) setCr(a.cr);
    }).catch(() => setAppt(false));
  }, [id]);

  useEffect(() => { const t = setInterval(() => setSeconds((s) => s + 1), 1000); return () => clearInterval(t); }, []);
  useEffect(() => () => { try { callRef.current?.destroy(); } catch { } }, []);

  // Renouvellement proactif de la session pendant une consultation active (toutes les 30 min)
  useEffect(() => {
    const id = setInterval(() => { refreshSession().catch(() => {}); }, 30 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const startCall = async () => {
    try {
      const { data } = await api.post(`/admin/visio/appointments/${id}/host-token`);
      setRoom(data);
      if (!data.simulated && data.room_url) {
        const Daily = await loadDaily();
        const call = Daily.createFrame(frameRef.current, { iframeStyle: { width: "100%", height: "440px", border: "0", borderRadius: "12px" }, showLeaveButton: true });
        await call.join({ url: data.room_url, token: data.token });
        callRef.current = call;
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveCr = async (terminer = false) => {
    setBusy(true);
    try {
      const payload = { ...cr, duree_reelle_min: Math.round(seconds / 60), terminer };
      if (terminer && boardPngRef.current) { try { payload.whiteboard_png_data = boardPngRef.current(); } catch {} }
      await api.post(`/admin/visio/appointments/${id}/cr`, payload);
      toast.success(terminer ? "Consultation terminée" : "Compte-rendu enregistré");
      if (terminer) setAppt({ ...appt, status: "termine" });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const genPdf = async () => {
    await saveCr(false);
    try { const res = await api.get(`/admin/visio/appointments/${id}/cr/pdf`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = `compte-rendu-${appt.name}.pdf`; a.click(); }
    catch { toast.error("PDF impossible"); }
  };
  const sendCr = async () => { await saveCr(false); try { const { data } = await api.post(`/admin/visio/appointments/${id}/cr/send`); toast.success(`Compte-rendu envoyé à ${data.sent_to}`); } catch (e) { toast.error(apiError(e)); } };

  const genDocx = async () => {
    await saveCr(false);
    try { const res = await api.get(`/admin/visio/appointments/${id}/cr/docx`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = `compte-rendu-${appt.name}.docx`; a.click(); }
    catch { toast.error("Word impossible"); }
  };

  const runAiSynthesis = async () => {
    await saveCr(false);
    setAiBusy(true);
    try {
      const { data } = await api.post(`/admin/visio/appointments/${id}/ai-synthesis`);
      setCr((prev) => ({ ...prev, synthese_ia: data.synthese }));
      toast.success("Synthèse IA générée");
    } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
  };

  const uploadTranscribe = async (field, blob) => {
    setRecBusy(field);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
      fd.append("file", blob, `dictee.${ext}`);
      const { data } = await api.post("/admin/visio/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const t = (data.text || "").trim();
      if (t) setCr((prev) => ({ ...prev, [field]: (prev[field] ? prev[field] + " " : "") + t }));
      else toast.info("Aucun texte détecté");
    } catch (e) { toast.error(apiError(e, "Transcription impossible")); } finally { setRecBusy(null); }
  };

  const startRec = async (field) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        await uploadTranscribe(field, blob);
      };
      mr.start(); mediaRecorderRef.current = mr; setRecField(field);
    } catch { toast.error("Microphone inaccessible — autorisez l'accès au micro."); }
  };
  const stopRec = () => { try { mediaRecorderRef.current?.stop(); } catch {} setRecField(null); };
  const toggleMic = (field) => { if (recField === field) stopRec(); else if (!recField && !recBusy) startRec(field); };

  const extractOrdonnance = (syn) => {
    if (!syn) return "";
    const lines = syn.split("\n");
    let start = -1;
    for (let i = 0; i < lines.length; i++) {
      if (/^#{1,3}\s/.test(lines[i]) && /ordonnance/i.test(lines[i])) { start = i; break; }
    }
    if (start === -1) return "";
    const out = [];
    for (let i = start + 1; i < lines.length; i++) {
      if (/^#{1,3}\s/.test(lines[i])) break;
      out.push(lines[i]);
    }
    return out.join("\n").replace(/\*\*/g, "").trim();
  };
  const integrateOrdonnance = () => {
    const ord = extractOrdonnance(cr.synthese_ia);
    if (!ord) { toast.error("Aucune ordonnance détectée dans la synthèse."); return; }
    setCr((prev) => ({ ...prev, ordonnance: ord }));
    toast.success("Ordonnance IA intégrée — vérifiez puis enregistrez.");
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  if (appt === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" /></div>;
  if (appt === false) return <div className="p-20 text-center text-slate-500">Session introuvable.</div>;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8" data-testid="teleconsultation-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold text-navy">Téléconsultation — {appt.name}</h1>
          <p className="text-sm text-slate-500">Dr. ANON A B · {new Date(appt.start).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</p>
        </div>
        <Button data-testid="teleconsult-end" onClick={() => { try { callRef.current?.destroy(); } catch {} window.close(); setTimeout(() => { window.location.href = "/admin"; }, 300); }}
          variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50">
          Mettre fin à la présentation
        </Button>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Panneau gauche : visio + outils */}
        <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="teleconsult-video">
          <div className="mb-3 flex items-center justify-between">
            <span className="flex items-center gap-1.5 rounded-full bg-navy px-3 py-1 font-mono text-xs text-white"><Clock size={13} /> {mmss}</span>
            <div className="flex items-center gap-2">
              {room && (
                <Button size="sm" variant="outline" data-testid="teleconsult-fullscreen" onClick={goFullscreen} className="rounded-full text-xs">
                  <Maximize2 size={13} className="mr-1" /> Plein écran
                </Button>
              )}
              <Button size="sm" variant="destructive" data-testid="teleconsult-end" onClick={() => saveCr(true)} className="rounded-full"><PhoneOff size={13} className="mr-1" /> Terminer</Button>
            </div>
          </div>
          <div ref={videoWrapRef} className="relative overflow-hidden rounded-xl bg-black/5">
          {isFs && (
            <div className="pointer-events-none absolute left-1/2 top-4 z-50 -translate-x-1/2 animate-pulse rounded-full bg-black/70 px-4 py-1.5 text-xs font-medium text-white shadow-lg" data-testid="teleconsult-fs-hint">
              Appuyez sur <kbd className="rounded bg-white/20 px-1.5 py-0.5 font-mono">Échap</kbd> pour quitter le plein écran
            </div>
          )}
          {!room ? (
            <div className="flex h-[440px] flex-col items-center justify-center rounded-xl bg-slate-50 text-center">
              <Video size={40} className="text-slate-300" />
              <Button data-testid="teleconsult-start" onClick={startCall} className="mt-4 rounded-full bg-ciel text-white hover:bg-sky-700">Démarrer la visioconférence</Button>
            </div>
          ) : room.simulated ? (
            <div className="flex h-[440px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 text-center text-sm text-slate-500" data-testid="teleconsult-simulated">
              <Video size={36} className="text-slate-400" /><p className="mt-3">Mode simulé — vidéo réelle dès la configuration de la clé Daily.co</p>
            </div>
          ) : <div ref={frameRef} className="min-h-[440px] w-full rounded-xl [&_iframe]:!h-full" data-testid="teleconsult-frame" />}
          </div>
        </div>
        <ConsultationDocs mode="host" aid={id} />
        <SessionTools token={appt.join_token} aid={id} isHost bindExport={(fn) => { boardPngRef.current = fn; }} />
        </div>
        {/* Panneau droit : dossier */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="teleconsult-dossier">
          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
            <b className="text-navy">{appt.name}</b>{appt.dob ? ` · Né(e) le ${appt.dob}` : ""}<br />
            <span className="text-slate-400">Motif :</span> {appt.motif || "—"}
          </div>
          <div className="mt-4 space-y-3">
            {EF.map((k) => (
              <div key={k}>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-slate-500">{LABELS[k]}</Label>
                  <button type="button" onClick={() => toggleMic(k)} disabled={recBusy === k || (recField && recField !== k)} data-testid={`teleconsult-mic-${k}`}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition ${recField === k ? "bg-red-100 text-red-600" : "text-ciel hover:bg-sky-50"} disabled:opacity-40`}
                    title="Dicter par la voix">
                    {recBusy === k ? <Loader2 size={12} className="animate-spin" /> : recField === k ? <Square size={12} /> : <Mic size={12} />}
                    {recBusy === k ? "Transcription…" : recField === k ? "Arrêter" : "Dicter"}
                  </button>
                </div>
                <Textarea data-testid={`teleconsult-${k}`} rows={k === "ordonnance" ? 3 : 2} value={cr[k] || ""} className="mt-1 text-sm" onChange={(e) => setCr({ ...cr, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          {cr.synthese_ia && (
            <div className="mt-4 rounded-xl border border-ciel/30 bg-sky-50/60 p-4" data-testid="teleconsult-synthesis">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-navy"><Sparkles size={15} className="text-ciel" /> Synthèse clinique assistée par IA</p>
              <div className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap text-xs text-slate-700" data-testid="teleconsult-synthesis-text">{cr.synthese_ia}</div>
              <Button data-testid="teleconsult-integrate-ordonnance" onClick={integrateOrdonnance} size="sm" className="mt-3 rounded-full bg-navy text-xs text-white hover:bg-navy-light"><FileText size={13} className="mr-1" /> Valider et intégrer l'ordonnance</Button>
            </div>
          )}
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="teleconsult-signature">
            <SignaturePad value={cr.signature_png || ""} onChange={(png) => setCr((prev) => ({ ...prev, signature_png: png }))} />
          </div>
          <div className="sticky bottom-0 z-30 -mx-5 mt-4 flex flex-wrap gap-2 border-t border-slate-100 bg-white/95 px-5 py-3 backdrop-blur" data-testid="teleconsult-actions">
            <Button data-testid="teleconsult-ai" onClick={runAiSynthesis} disabled={aiBusy} className="rounded-full bg-ciel text-xs text-white hover:bg-sky-700">{aiBusy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />} Analyse IA</Button>
            <Button data-testid="teleconsult-save" onClick={() => saveCr(false)} disabled={busy} variant="outline" className="rounded-full text-xs"><Save size={13} className="mr-1" /> Enregistrer</Button>
            <Button data-testid="teleconsult-pdf" onClick={genPdf} className="rounded-full bg-navy text-xs text-white hover:bg-navy-light"><FileDown size={13} className="mr-1" /> CR PDF</Button>
            <Button data-testid="teleconsult-docx" onClick={genDocx} variant="outline" className="rounded-full text-xs"><FileText size={13} className="mr-1" /> CR Word</Button>
            <Button data-testid="teleconsult-send" onClick={sendCr} variant="outline" className="rounded-full text-xs"><Send size={13} className="mr-1" /> Envoyer au patient</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
